import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import useCountdown from 'hooks/useCountdown';
import { ButtonType } from 'components/ui/Button';
import RadioBullet from 'components/ui/RadioBullet';
import { ChannelEnum } from 'modules/auth/types/LoginRequest';
import {
  Button, Modal,
} from 'components/ui';
import { getAvailableChannels, getDefaultChannelToSendCode, getRetryExpiration } from 'modules/communication/utils';
import { CommunicationState } from '../../modules/communication/types/CommunicationState';

interface SelectCommunicationChannelModalProps {
  onSubmit: (channel: ChannelEnum) => void | Promise<void>;
  onClose: (value: boolean) => void;
  blackListChannels?: ChannelEnum[];
  maxAttemptReached?: boolean;
  title: MessageDescriptor;
  message?: MessageDescriptor;
  sendToChannelMessage: MessageDescriptor;
  withCountdown?: boolean;
  data: CommunicationState;
}

const USED_FIELD_BY_CHANNEL = {
  [ChannelEnum.SMS]: 'phoneNumber',
  [ChannelEnum.WHATSAPP]: 'phoneNumber',
  [ChannelEnum.EMAIL]: 'email',
} as const satisfies Record<ChannelEnum, keyof CommunicationState>;

const SelectCommunicationChannelModal: React.FC<SelectCommunicationChannelModalProps> = ({
  onSubmit,
  onClose,
  blackListChannels,
  maxAttemptReached,
  sendToChannelMessage,
  title,
  message,
  withCountdown,
  data,
}) => {
  const ref = React.useRef<HTMLButtonElement>(null);

  const [isLoading, setIsLoading] = React.useState(false);

  const [channel, setChannel] = React.useState(getDefaultChannelToSendCode(data, blackListChannels));
  const { countdown: timeRemaining } = useCountdown({ date: getRetryExpiration(data, channel) });

  const availableChannels = React.useMemo(() => getAvailableChannels(data), [data]);
  const isDisabled = isLoading || (withCountdown && timeRemaining > 0) || maxAttemptReached;

  const handleClose = React.useCallback(() => onClose(false), [onClose]);

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsLoading(true);
      await onSubmit(channel);
      handleClose();
    } catch {
      // do nothing, modal stays opened
    }
    setIsLoading(false);
  }, [onSubmit, channel, handleClose]);

  const handleChange = (value: ChannelEnum) => {
    setChannel(value);
  };

  return (
    <Modal
      open
      focusRef={ref}
      className="modal-large"
      onClose={onClose}
      title={<FormattedMessage tagName="h2" {...title} />}
    >
      <div className="flex flex-col items-center mt-5" data-testid="resendModal">
        {message && (
          <p className="mb-3xs">
            <FormattedMessage {...message} values={{ channel: getDefaultChannelToSendCode(data) }} />
          </p>
        )}
        {availableChannels.map((availableChannel: ChannelEnum) => {
          const channelLower = availableChannel.toLowerCase();
          const field = USED_FIELD_BY_CHANNEL[availableChannel];
          return (
            <div className="w-full" key={availableChannel}>
              <RadioBullet
                name="channel"
                value={availableChannel}
                checked={channel === availableChannel}
                onChange={handleChange}
                data-testid={`${channelLower}-radio`}
                title={(
                  <FormattedMessage
                    id={`login.resend.${channelLower}.title`}
                    defaultMessage={availableChannel}
                  />
                )}
                description={(
                  <FormattedMessage
                    id={`login.resend.${channelLower}.description`}
                    defaultMessage={`${availableChannel} sent to {${field}}`}
                    values={{ [field]: data[field] }}
                  />
                )}
              />
            </div>
          );
        })}
        {maxAttemptReached && (
          <span className="text-center mb-4">
            <FormattedMessage
              id="login.resend.reachedMaxAttempts"
              defaultMessage="You reached the maximum number of attempts."
            />
          </span>
        )}
        {!maxAttemptReached && (
          <Button
            ref={ref}
            loading={isLoading}
            onClick={handleSubmit}
            className="w-full mt-3xs mb-2xs"
            disabled={isDisabled}
            testId="sendToChannel"
          >
            <FormattedMessage {...sendToChannelMessage} values={{ countdown: timeRemaining, channel }} />
          </Button>
        )}
        <Button onClick={handleClose} type={ButtonType.SECONDARY} className="w-full" testId="cancel-button">
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
      </div>
    </Modal>
  );
};

export default SelectCommunicationChannelModal;
